import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import type { BrandingKey, BrandingTextKey, BrandingJsonKey, FontPairing } from "@/lib/branding-keys";

export interface BrandingRow {
  key: string;
  value_text: string | null;
  value_json: unknown | null;
  updated_at: string;
  updated_by: string | null;
}

export interface BrandingMap {
  text: Record<string, string>;
  json: Record<string, unknown>;
}

function toMap(rows: BrandingRow[]): BrandingMap {
  const text: Record<string, string> = {};
  const json: Record<string, unknown> = {};
  for (const r of rows) {
    if (r.value_json !== null && r.value_json !== undefined) {
      json[r.key] = r.value_json;
    }
    if (r.value_text !== null && r.value_text !== undefined) {
      text[r.key] = r.value_text;
    }
  }
  return { text, json };
}

export function useBranding() {
  const query = useQuery({
    queryKey: ["site-branding"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_branding")
        .select("*");
      if (error) throw error;
      return (data ?? []) as BrandingRow[];
    },
    staleTime: 5 * 60_000,
  });

  const map = useMemo<BrandingMap>(() => toMap(query.data ?? []), [query.data]);

  function getText(key: BrandingTextKey, fallback = ""): string {
    return map.text[key] ?? fallback;
  }

  function getJson<T = unknown>(key: BrandingJsonKey, fallback: T): T {
    const val = map.json[key];
    return (val as T) ?? fallback;
  }

  function getFont(key: "typography.heading" | "typography.body"): FontPairing | null {
    const val = map.json[key];
    if (val && typeof val === "object" && "family" in val) {
      return val as FontPairing;
    }
    return null;
  }

  return {
    ...query,
    map,
    getText,
    getJson,
    getFont,
  };
}

type UpsertInput =
  | { key: BrandingTextKey; value_text: string; value_json?: null }
  | { key: BrandingJsonKey; value_json: unknown; value_text?: null };

export function useUpsertBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertInput | UpsertInput[]) => {
      const list = Array.isArray(input) ? input : [input];
      const { error } = await supabase
        .from("site_branding")
        .upsert(
          list.map((r) => ({
            key: r.key,
            value_text: "value_text" in r ? r.value_text ?? null : null,
            // value_json is typed `unknown` on the input union; the column is
            // Json, so cast at the boundary.
            value_json: ("value_json" in r ? r.value_json ?? null : null) as Json,
          })),
          { onConflict: "key" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site-branding"] });
    },
  });
}

// Convenience for "set a single text value"
export function useUpsertBrandingText() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: BrandingKey; value: string }) => {
      const { error } = await supabase
        .from("site_branding")
        .upsert({ key, value_text: value, value_json: null }, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site-branding"] });
    },
  });
}
